/**
 * Contact page.
 *
 * The form here is the same short one used in every hero, plus a "tell us
 * more" box. It replaced an eleven-field version: property type, floor area,
 * bedrooms, preferred date and promotion were all being asked before the
 * business had said hello, and every one of those fields costs completions on
 * a first contact. The detail is easier to gather in the reply, once someone
 * has already put their hand up.
 */
import { ArrowUpRight, Phone, Mail, MessageCircle } from "lucide-react";
import PageHero from "../components/PageHero";
import Eyebrow from "../components/shared/Eyebrow";
import HeroQuoteForm from "../components/shared/HeroQuoteForm";
import { useBrand } from "../context/SiteSettingsContext";

export default function Contact() {
  const BRAND = useBrand();

  return (
    <>
      {/*
        No quote form in the hero on this page — the form is the page. Two
        identical forms a scroll apart would just raise the question of which
        one is the real one.
      */}
      <PageHero
        eyebrow="Contact"
        headline={"Tell us about\nyour property."}
        intro="Share a few details and we'll come back to you with a quotation, usually within one working day."
        image="/generated/hero_about.png"
        imageAlt="Emilio's Solutions team with equipment"
        showQuoteForm={false}
        compact
      />

      <section
        className="bg-ink py-20 md:py-28 border-t border-charcoal/50"
        data-testid="contact-page"
      >
        <div className="container-emilio grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <Eyebrow>Request a Quotation</Eyebrow>
            <div className="mt-6">
              <HeroQuoteForm heading="Get Your Free Quote" showMessage />
            </div>
          </div>

          <aside className="lg:col-span-5 space-y-6">
            <div className="border border-charcoal p-6 md:p-8 bg-charcoal/20">
              <div className="text-eyebrow font-display font-bold uppercase tracking-[0.22em] text-emerald">
                Reach Us
              </div>
              <div className="mt-5 space-y-3 text-sm">
                <a
                  href={`tel:${BRAND.phoneRaw}`}
                  className="flex items-center gap-3 text-white hover:text-emerald"
                >
                  <Phone className="w-4 h-4 text-emerald" /> {BRAND.phone}
                </a>
                <a
                  href={`mailto:${BRAND.email}`}
                  className="flex items-center gap-3 text-white hover:text-emerald"
                >
                  <Mail className="w-4 h-4 text-emerald" /> {BRAND.email}
                </a>
              </div>
              <a
                href={BRAND.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-flex items-center gap-2 rounded-full border border-white/20 hover:border-emerald hover:text-emerald text-white px-4 py-2.5 text-sm font-semibold transition-colors"
              >
                Open WhatsApp <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>

            {/*
              What to send on WhatsApp, taken from the client's content
              document. Photos are what make a quotation accurate, so it is
              worth asking for them explicitly rather than hoping.
            */}
            <div className="border border-charcoal p-6 md:p-8">
              <div className="text-eyebrow font-display font-bold uppercase tracking-[0.22em] text-emerald flex items-center gap-2">
                <MessageCircle className="w-4 h-4" /> Prefer WhatsApp?
              </div>
              <p className="mt-3 text-sm text-mutedtext">
                For a faster assessment, send us:
              </p>
              <ol className="mt-3 space-y-1.5 text-sm text-white/85 list-decimal list-inside">
                <li>Service required</li>
                <li>Property type and size</li>
                <li>Location</li>
                <li>Preferred date</li>
                <li>Photos or videos of the areas needing cleaning</li>
              </ol>
            </div>

            <div className="border border-charcoal p-6 md:p-8">
              <div className="text-eyebrow font-display font-bold uppercase tracking-[0.22em] text-emerald">
                Operating Area
              </div>
              <p className="mt-3 text-sm text-mutedtext leading-relaxed">
                Islandwide service throughout Singapore. HDB, BTO, condos,
                landed homes, offices, retail, F&amp;B and light commercial.
              </p>
              <div className="mt-6 text-xs text-mutedtext">UEN: {BRAND.uen}</div>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}
